import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../Components/SEO';
import HeroBackground from '../Components/MainPageVideo';
import AutomovelBox from '../Components/AutomovelBox';
import AutomoveisGrid from '../Components/AutomoveisGrid';
import LoadingSpinner from '../Components/LoadingSpinner';
import { useCar } from '../stores/CarContext';
import { FaArrowRight, FaInstagram, FaCar, FaWrench, FaSprayCan, FaGem, FaTools } from 'react-icons/fa';
import esteticaBg from '../assets/estetica-bg.webp';
import { PAGE_SEO, SOCIAL_LINKS, getHomeStructuredData } from '../seoConfig';

const MARQUEE_ITEMS = [
    'Compra & Venda', 'Semi-Novos', 'Usados', 'Mecânica Ligeira',
    'Inspeções', 'Lavagem Auto', 'Polimentos', 'Cerâmica', '@autojoaopirespt',
];

const SERVICES = [
    { icon: FaCar, num: '01', title: 'Compra & Venda', desc: 'Automóveis semi-novos e usados selecionados com transparência total.' },
    { icon: FaTools, num: '02', title: 'Mecânica Ligeira', desc: 'Manutenção preventiva, diagnóstico e reparações com rigor técnico.' },
    { icon: FaWrench, num: '03', title: 'Manutenção & Inspeções', desc: 'Preparação completa para inspeções e revisões periódicas.' },
    { icon: FaSprayCan, num: '04', title: 'Higienização & Lavagem', desc: 'Lavagem automóvel e higienização profunda do habitáculo.' },
    { icon: FaGem, num: '05', title: 'Polimentos & Cerâmica', desc: 'Proteção duradoura e brilho de showroom para o seu carro.' },
];

const MainPage = () => {
    const { carsPPB, fetchCarsPPB, isLoading, error } = useCar();
    const navigate = useNavigate();
    const featuredCars = carsPPB ? carsPPB.filter(car => Number(car.vendido) !== 1).slice(0, 8) : [];

    useEffect(() => { fetchCarsPPB(); }, [fetchCarsPPB]);

    const goStock = () => { navigate('/automoveis'); window.scrollTo(0, 0); };

    if (error) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-10 text-center">
                <div className="space-y-4">
                    <div className="font-display text-6xl text-red/30">!</div>
                    <h2 className="font-display text-2xl text-white tracking-widest">Sem Ligação</h2>
                    <p className="text-white/40 text-sm">Verifica a internet e tenta novamente.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-black text-white min-h-screen overflow-x-hidden">
            <SEO
                title={PAGE_SEO.home.title}
                description={PAGE_SEO.home.description}
                path={PAGE_SEO.home.path}
                structuredData={getHomeStructuredData()}
            />

            {/* ── HERO ── */}
            <section className="relative min-h-screen flex items-end lg:items-center overflow-hidden">
                <div className="absolute inset-0">
                    <HeroBackground />
                </div>
                <div className="ajp-grain absolute inset-0 z-[1]" />

                {/* Vertical accent */}
                <div className="hidden lg:block absolute left-12 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-red/30 to-transparent z-[2]" />

                <div className="relative z-10 w-full max-w-[1500px] mx-auto px-5 lg:px-12 pt-28 pb-16 lg:pb-0">
                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end lg:items-center min-h-[80vh]">
                        <div className="lg:col-span-7 space-y-8">
                            <div className="space-y-4 animate-fade-up" style={{ opacity: 0, animationFillMode: 'forwards' }}>
                                <p className="section-tag">Auto João Pires · Stand Automóvel</p>
                                <h1 className="font-display text-[clamp(2.75rem,7.5vw,6.5rem)] text-white leading-[0.92] tracking-wide">
                                    COMPRA & VENDA DE<br />
                                    <span className="text-gradient">AUTOMÓVEIS</span><br />
                                    SEMI-NOVOS & USADOS
                                </h1>
                            </div>

                            <p
                                className="text-white/55 text-base sm:text-xl max-w-xl leading-relaxed font-medium animate-fade-up border-l-2 border-red pl-5"
                                style={{ animationDelay: '120ms', opacity: 0, animationFillMode: 'forwards' }}
                            >
                                Mecânica ligeira, manutenção e inspeções. Higienização, lavagem auto, polimentos e cerâmica.
                            </p>

                            <div
                                className="flex flex-wrap gap-4 animate-fade-up"
                                style={{ animationDelay: '220ms', opacity: 0, animationFillMode: 'forwards' }}
                            >
                                <button onClick={goStock} className="btn-primary">
                                    Explorar Stock <FaArrowRight size={10} />
                                </button>
                                <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" className="btn-secondary">
                                    <FaInstagram size={12} /> Instagram
                                </a>
                            </div>
                        </div>

                        <div className="lg:col-span-5 hidden lg:flex flex-col items-end gap-6 animate-fade-up" style={{ animationDelay: '400ms', opacity: 0, animationFillMode: 'forwards' }}>
                            {SERVICES.slice(0, 3).map((s) => (
                                <div key={s.num} className="flex items-center gap-4 text-right w-full max-w-xs">
                                    <div className="flex-1">
                                        <div className="text-[9px] font-bold uppercase tracking-[0.3em] text-red/50">{s.num}</div>
                                        <div className="font-display text-xl text-white tracking-widest">{s.title}</div>
                                    </div>
                                    <div className="w-10 h-10 border border-red/20 flex items-center justify-center text-red shrink-0">
                                        <s.icon size={14} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
                    <span className="text-[9px] font-bold uppercase tracking-[0.4em] text-white/20">Scroll</span>
                    <div className="w-px h-12 bg-gradient-to-b from-red/60 to-transparent animate-pulse-gold" />
                </div>
            </section>

            {/* ── MARQUEE ── */}
            <div className="border-y border-red/20 bg-red py-3 overflow-hidden">
                <div className="flex animate-marquee whitespace-nowrap">
                    {[...Array(2)].map((_, ri) => (
                        <div key={ri} className="flex">
                            {MARQUEE_ITEMS.map((item, i) => (
                                <span key={i} className="flex items-center mx-8 text-[11px] font-bold uppercase tracking-[0.3em] text-black">
                                    {item}
                                    <span className="ml-8 text-black/30">◆</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* ── STOCK ── */}
            <section className="ajp-section-alt relative py-24 lg:py-32 overflow-hidden border-t border-white/[0.06]">
                <div className="ajp-grain absolute inset-0" />
                <div className="relative z-10 max-w-[1500px] mx-auto px-5 lg:px-12">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
                        <div className="flex items-end gap-6">
                            <span className="ajp-num hidden sm:block">01</span>
                            <div>
                                <p className="section-tag mb-3">Seleção</p>
                                <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide">
                                    EM <span className="text-gradient">DESTAQUE</span>
                                </h2>
                            </div>
                        </div>
                        <button onClick={goStock} className="btn-ghost self-start lg:self-auto">
                            Ver todo o stock <FaArrowRight size={10} />
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="py-32 flex justify-center"><LoadingSpinner /></div>
                    ) : featuredCars.length > 0 ? (
                        <AutomoveisGrid>
                            {featuredCars.map((carro, i) => (
                                <AutomovelBox
                                    key={carro.id || i}
                                    carId={carro.id}
                                    carIndex={i}
                                    carImage={carro.thumb_url}
                                    title={carro.titulo}
                                    price={carro.preco}
                                    kms={carro.kms}
                                    year={carro.ano}
                                    vendido={carro.vendido}
                                    brevemente={carro.brevemente}
                                    instagramShortcode={carro.instagramShortcode}
                                />
                            ))}
                        </AutomoveisGrid>
                    ) : (
                        <p className="text-white/30 text-sm">Stock em breve disponível.</p>
                    )}
                </div>
            </section>

            {/* ── SERVIÇOS ── */}
            <section className="relative border-t border-white/[0.06] bg-black">
                <div className="grid lg:grid-cols-2">
                    <div className="relative p-8 lg:p-16 lg:py-24 border-b lg:border-b-0 lg:border-r border-white/[0.06] ajp-section-alt">
                        <div className="ajp-grain absolute inset-0" />
                        <div className="relative z-10">
                            <span className="ajp-num absolute -top-4 -left-2 opacity-30 hidden lg:block">02</span>
                            <p className="section-tag mb-4">Oficina & Stand</p>
                            <h2 className="font-display text-5xl sm:text-6xl text-white tracking-wide mb-6">
                                TUDO PARA<br /><span className="text-gradient">O SEU CARRO</span>
                            </h2>
                            <p className="text-white/40 text-sm leading-relaxed max-w-sm mb-10">
                                Do momento da compra à manutenção e estética — um só lugar, uma equipa de confiança.
                            </p>
                            <a
                                href={`${SOCIAL_LINKS.whatsapp}?text=${encodeURIComponent('Olá, gostaria de agendar um serviço.')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-primary"
                            >
                                <FaInstagram size={12} /> Agendar Serviço
                            </a>
                        </div>
                    </div>

                    <div className="divide-y divide-white/[0.06]">
                        {SERVICES.map((s) => (
                            <div
                                key={s.num}
                                className="group flex items-start gap-6 p-8 lg:p-10 hover:bg-white/[0.02] transition-colors duration-300 cursor-default"
                            >
                                <span className="font-display text-3xl text-red/30 group-hover:text-red transition-colors shrink-0">{s.num}</span>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <s.icon size={12} className="text-red" />
                                        <h3 className="font-display text-xl text-white tracking-widest group-hover:text-red transition-colors">{s.title}</h3>
                                    </div>
                                    <p className="text-white/35 text-sm leading-relaxed">{s.desc}</p>
                                </div>
                                <FaArrowRight size={10} className="text-white/10 group-hover:text-red mt-1 transition-all group-hover:translate-x-1" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── ESTÉTICA VISUAL ── */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden">
                <img
                    src={esteticaBg}
                    alt="Serviços de estética automóvel"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-black/75" />
                <div className="ajp-grain absolute inset-0" />

                <div className="relative z-10 max-w-[1500px] mx-auto px-5 lg:px-12 py-24 w-full">
                    <div className="max-w-xl">
                        <p className="section-tag mb-4">Estética Automóvel</p>
                        <h2 className="font-display text-5xl sm:text-7xl text-white tracking-wide mb-6">
                            BRILHO DE<br /><span className="text-gradient">SHOWROOM</span>
                        </h2>
                        <p className="text-white/50 text-base leading-relaxed mb-8">
                            Higienização, lavagem, polimentos e cerâmica com produtos premium e atenção ao detalhe.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            {['Lavagem', 'Polimento', 'Cerâmica', 'Interiores'].map((tag) => (
                                <span key={tag} className="px-4 py-2 border border-white/15 text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA FINAL ── */}
            <section className="relative border-t border-red/20 bg-red py-16 lg:py-20">
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-8">
                    <div>
                        <h2 className="font-display text-4xl sm:text-5xl text-black tracking-wide">
                            PRONTO PARA<br />ENCONTRAR O SEU CARRO?
                        </h2>
                    </div>
                    <div className="flex flex-wrap gap-4 shrink-0">
                        <button onClick={goStock} className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-black transition-colors">
                            Ver Stock <FaArrowRight size={10} />
                        </button>
                        <a
                            href={SOCIAL_LINKS.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-black text-black text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors"
                        >
                            <FaInstagram size={12} /> @autojoaopirespt
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MainPage;
